import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  usePhoneVerificationControllerSendMutation,
  usePhoneVerificationControllerStatusQuery,
  usePhoneVerificationControllerVerifyMutation,
} from '@/services/generated/api';
import { isFetchBaseQueryError } from '@/services/baseApi';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { CenteredCardLayout } from '@/components/ui/centered-card-layout';
import { Input } from '@/components/ui/input';

/**
 * Screen 3b of `docs/DIGITAL_ONBOARDING.md`: the number the SII's answer is
 * announced on.
 *
 * ## Why this is asked before the product is visible
 *
 * Unlike the address code, this one is where it is on purpose. Certification
 * runs for days and nobody watches a dashboard for days, so the number is what
 * makes the answer reach anybody at all. And a sign-up that costs a working
 * phone number is not one somebody makes in bulk to read a competitor's
 * product — which is the whole reason it sits ahead of the wizard rather than
 * inside it.
 *
 * ## Why there is no way past it
 *
 * «Lo hago después» was removed: a filter somebody can decline is not a filter,
 * and the number is the only channel the SII's answer reaches anybody on.
 *
 * The one case that is NOT a refusal by the person is an environment with no
 * provider configured, where the code is never delivered and demanding it would
 * be a trap rather than a gate. The server takes the step out of the sequence
 * then (`phoneVerificationAvailable`), and this screen says so instead of
 * leaving somebody waiting for a message that is not coming.
 *
 * ## Two steps on one screen
 *
 * The number is collected here rather than at sign-up, so there is never a
 * number on file that nobody has proved. Asking for it in the same place it is
 * verified also means a typo is fixed where it was made, instead of sending
 * somebody back a screen.
 */
const CODE_LENGTH = 6;

const TCIT_BRAND = <span className="text-h3 font-extrabold text-fg">TCIT</span>;

const GENERIC_ERROR = 'No pudimos enviar el código. Inténtalo de nuevo.';

/**
 * Screen 4, which is what the entry sequence ends on. Somebody who has already
 * answered it is sent straight past it, so pointing here is right whether or
 * not they have.
 */
const NEXT = '/segment';

/** The backend's message is written for this screen — see `VerifyEmailPage`. */
function errorMessage(error: unknown): string {
  if (!isFetchBaseQueryError(error)) return GENERIC_ERROR;
  const data = error.data as { message?: unknown } | undefined;
  if (typeof data?.message === 'string') return data.message;
  if (Array.isArray(data?.message) && typeof data.message[0] === 'string') return data.message[0];
  return GENERIC_ERROR;
}

const CHANNEL_LABEL: Record<string, string> = {
  whatsapp: 'WhatsApp',
  sms: 'SMS',
};

export function VerifyPhonePage(): React.JSX.Element {
  const navigate = useNavigate();
  const { data: status } = usePhoneVerificationControllerStatusQuery();
  const [send, { data: issued, error: sendError, isLoading: isSending }] =
    usePhoneVerificationControllerSendMutation();
  const [verify, { error: verifyError, isLoading: isVerifying }] =
    usePhoneVerificationControllerVerifyMutation();

  const [phone, setPhone] = React.useState('');
  const [code, setCode] = React.useState('');
  const [resendIn, setResendIn] = React.useState(0);

  React.useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  // Somebody who already proved a number has nothing to do here.
  React.useEffect(() => {
    if (status?.verified) navigate(NEXT, { replace: true });
  }, [status, navigate]);

  async function handleSend(event: React.FormEvent, channel: 'whatsapp' | 'sms'): Promise<void> {
    event.preventDefault();
    const result = await send({ sendPhoneCodeDto: { phone, channel } });
    if ('data' in result && result.data) setResendIn(result.data.resendAfterSeconds);
  }

  async function handleVerify(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const result = await verify({ verifyPhoneDto: { code } });
    if (!('error' in result)) navigate(NEXT, { replace: true });
  }

  // The code field appears only once a code exists to type into it: two fields
  // at once would ask somebody to fill in the answer before the question.
  const codeSent = issued !== undefined;
  const error = verifyError ?? sendError;

  // No provider in this environment: the code would never arrive, so the screen
  // says so and moves on rather than asking for a number it cannot use. The
  // sequence has already taken this step out — see `OnboardingGate`.
  if (status && status.available === false) {
    return (
      <CenteredCardLayout
        title="Confirma tu teléfono"
        description="Este paso no está disponible todavía."
        brand={TCIT_BRAND}
      >
        <div className="flex flex-col gap-stack">
          <Callout tone="info" title="Todavía no podemos enviarte el código">
            Estamos terminando de configurar el envío por WhatsApp. Puedes seguir y lo
            confirmamos más adelante.
          </Callout>
          <Button type="button" className="w-full" onClick={() => navigate(NEXT, { replace: true })}>
            Continuar
          </Button>
        </div>
      </CenteredCardLayout>
    );
  }

  return (
    <CenteredCardLayout
      title="Confirma tu teléfono"
      description={
        codeSent
          ? `Te mandamos un código por ${CHANNEL_LABEL[issued.channel] ?? issued.channel} al ${issued.phone}.`
          : 'Es el número por el que te avisamos cuando el SII responda. La certificación puede tardar días y nadie mira un panel por días.'
      }
      brand={TCIT_BRAND}
    >
      {error ? (
        <Callout tone="danger" title="No pudimos confirmar tu teléfono" className="mb-stack">
          {errorMessage(error)}
        </Callout>
      ) : null}

      {codeSent ? (
        <form onSubmit={handleVerify} className="flex flex-col gap-stack">
          <div>
            <label htmlFor="phone-code" className="mb-1 block text-body-s font-medium">
              Código
            </label>
            <Input
              id="phone-code"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={CODE_LENGTH}
              placeholder="492 718"
              className="text-center text-h3 tracking-[0.35em]"
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={code.length !== CODE_LENGTH || isVerifying}
          >
            {isVerifying ? 'Confirmando…' : 'Confirmar'}
          </Button>

          <p className="text-center text-body-s text-fg-muted">
            {resendIn > 0 ? (
              `Puedes pedir otro código en ${resendIn} s.`
            ) : (
              <button
                type="button"
                onClick={(event) => handleSend(event, 'sms')}
                disabled={isSending}
                className="font-medium text-action hover:text-action-hover disabled:text-fg-disabled"
              >
                Mándamelo por SMS
              </button>
            )}
          </p>
        </form>
      ) : (
        <form onSubmit={(event) => handleSend(event, 'whatsapp')} className="flex flex-col gap-stack">
          <div>
            <label htmlFor="phone" className="mb-1 block text-body-s font-medium">
              Teléfono
            </label>
            <Input
              id="phone"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+56 9 8765 4321"
            />
            <p className="mt-1 text-body-s text-fg-muted">
              Con código de país. Te llega por WhatsApp.
            </p>
          </div>

          <Button type="submit" className="w-full" disabled={phone.trim().length < 8 || isSending}>
            {isSending ? 'Enviando…' : 'Enviarme el código'}
          </Button>
        </form>
      )}
    </CenteredCardLayout>
  );
}
